import React, { useMemo, useEffect, useRef, useState } from "react";
import { Container, Card, Table, Badge, Placeholder, Button } from "react-bootstrap";
import { useParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDepartmentReports } from "../hooks/useDepartmentReports";
import { fetchDepartmentReportStatuses } from "../api/departmentReportApi";
import { DepartmentReportStatus, LeadershipType } from "../types/departmentReport";
import { FiFileText, FiCheckCircle, FiRefreshCw } from "react-icons/fi";
import { Pagination } from "@core/components/Pagination";
import { SearchBar } from "@core/components/SearchBar";
import NoResult from "@core/components/NoResult";
import EntityCard from "@core/components/EntityCard";
import TableActions from "@core/components/TableActions";
import TableActionButton from "@core/components/TableActionButton";
import { subtleBadgeProps } from "@core/utils/subtleBadge";
import { useDebounce } from "@core/hooks/useDebounce";
import { useSearchUrlSync } from "@core/hooks/useSearchUrlSync";
import { useDepartments } from "@features/department/hooks/useDepartments";
import { useReportTags } from "@features/report/tag/hooks/useReportTags";
import { processDepartmentReportAsync } from "@core/reports/api/processDepartmentReport";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { ErrorResponse } from "@core/types/errorResponse";
import {
    canValidateDepartmentReport,
    getReportTagStatusLabel,
    getWorkflowStatusLabel,
    getWorkflowStatusVariant,
    isDepartmentReportEditable,
    isTagOpen,
    normalizeReportWorkflowStatus,
} from "@core/reports/utils/reportWorkflow";
import {
    REPORT_SPA_QUERY_PARAMS,
    buildReportListFilterSearch,
    buildReportViewSearch,
    parseReportListFilterSearch,
} from "@core/reports/constants/reportSpaQueryParams";

// ─── Static config ────────────────────────────────────────────────────────────

const SLUG_TO_STATUS_NAME: Record<string, string> = {
    draft: "DRAFT",
    submitted: "SUBMITTED",
    "needs-attention": "NEEDS_ATTENTION",
    "awaiting-approval": "AWAITING_APPROVAL",
    approved: "APPROVED",
};

const SLUG_LABELS: Record<string, { label: string; variant: string }> = {
    all: { label: "All", variant: "secondary" },
    draft: { label: "Draft", variant: getWorkflowStatusVariant("DRAFT") },
    submitted: { label: "Submitted", variant: getWorkflowStatusVariant("SUBMITTED") },
    "needs-attention": {
        label: "Needs Attention",
        variant: getWorkflowStatusVariant("NEEDS_ATTENTION"),
    },
    "awaiting-approval": {
        label: "Awaiting Approval",
        variant: getWorkflowStatusVariant("AWAITING_APPROVAL"),
    },
    approved: { label: "Approved", variant: getWorkflowStatusVariant("APPROVED") },
};

type ScopeType = 'CHURCH' | 'DISTRICT' | 'NATION';

// Human-readable heading per leadership type (D11 reuse — same list, different framing).
const LEADERSHIP_TITLES: Record<LeadershipType, string> = {
    PASTOR: "Leadership Reports — Pastor",
    DISTRICT_OVERSEER: "Leadership Reports — District Overseer",
    GENERAL: "Leadership Reports — Finance & Statistics",
};

// URL segment used for the leadership sub-area, keyed by leadership type.
// GENERAL (the GENERAL leadership department) surfaces as the Finance & Statistics report.
const LEADERSHIP_SLUGS: Record<LeadershipType, string> = {
    PASTOR: "pastor",
    DISTRICT_OVERSEER: "district-overseer",
    GENERAL: "finance-statistics",
};

type DepartmentReportsListProps = {
    /** When set, this list shows leadership reports of the given type (D11) and routes View to the pastor pages. */
    leadershipType?: LeadershipType;
};

// ─── Component ────────────────────────────────────────────────────────────────

const DepartmentReportsList: React.FC<DepartmentReportsListProps> = ({ leadershipType }) => {
    const { churchId, districtId, nationId } = useParams<{
        churchId?: string;
        districtId?: string;
        nationId?: string;
    }>();
    const location = useLocation();
    const navigate = useNavigate();

    // ── Status list (needed to resolve slug → reportStatusId) ──────────────
    const [statuses, setStatuses] = useState<DepartmentReportStatus[]>([]);
    const [statusesLoading, setStatusesLoading] = useState(true);

    useEffect(() => {
        fetchDepartmentReportStatuses()
            .then((res) => {
                if (res.isSuccess()) {
                    const payload = res.getData();
                    const items = Array.isArray(payload) ? payload : payload?.data ?? [];
                    setStatuses(items);
                }
            })
            .catch(() => { /* silently handle */ })
            .finally(() => { setStatusesLoading(false); });
    }, []);

    // ── Scope from URL path params ──────────────────────────────────────────
    const { scope, scopeId } = useMemo((): { scope: ScopeType; scopeId: number } => {
        if (churchId) return { scope: 'CHURCH', scopeId: Number(churchId) };
        if (districtId) return { scope: 'DISTRICT', scopeId: Number(districtId) };
        if (nationId) return { scope: 'NATION', scopeId: Number(nationId) };
        return { scope: 'CHURCH', scopeId: 0 };
    }, [churchId, districtId, nationId]);

    // Base path used for "View All" navigation link
    const basePath = useMemo(() => {
        if (scope === 'NATION') return `/nation/${scopeId}`;
        if (scope === 'DISTRICT') return `/district/${scopeId}`;
        return `/church/${scopeId}`;
    }, [scope, scopeId]);

    // ── Status slug derived from the last path segment ──────────────────────
    // e.g. /church/1/department-reports/draft → "draft"
    const statusSlug = useMemo(() => {
        const segments = location.pathname.split("/").filter(Boolean);
        return segments[segments.length - 1] || "draft";
    }, [location.pathname]);

    const slugLabel = SLUG_LABELS[statusSlug] ?? SLUG_LABELS.draft;
    const isAllView = statusSlug === "all";
    const targetStatusName = SLUG_TO_STATUS_NAME[statusSlug] ?? "DRAFT";

    // Resolve reportStatusId from the fetched status list
    const reportStatusId = useMemo(() => {
        if (isAllView) return undefined;
        const match = statuses.find(
            (s) => s.status_name.toUpperCase() === targetStatusName
        );
        return match?.report_status_id;
    }, [statuses, targetStatusName, isAllView]);

    // ── Search state (initialised from URL) ────────────────────────────────
    const [urlParams] = useSearchParams();
    const [searchFreetext, setSearchFreetext] = useState(() => urlParams.get('q') ?? '');
    const debouncedFreetext = useDebounce(searchFreetext, 300);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [deptFilterId, setDeptFilterId] = useState<number | undefined>(() => {
        // Leadership lists are scoped by leadershipType — do not seed department from URL.
        if (leadershipType) return undefined;
        return parseReportListFilterSearch(urlParams).departmentId;
    });
    const [reportTagFilterId, setReportTagFilterId] = useState<number | undefined>(() => {
        return parseReportListFilterSearch(urlParams).reportTagId;
    });
    const [processingKey, setProcessingKey] = useState<string | null>(null);
    const previousPathnameRef = useRef(location.pathname);
    const showError = useApiError();

    // Sync search state back to URL (bounded to this page — status stays in path)
    useSearchUrlSync({
        query: debouncedFreetext,
        filters: buildReportListFilterSearch({
            departmentId: leadershipType ? undefined : deptFilterId,
            reportTagId: reportTagFilterId,
        }),
        page,
    });

    useEffect(() => {
        if (previousPathnameRef.current === location.pathname) return;

        previousPathnameRef.current = location.pathname;
        setSearchFreetext('');
        setDeptFilterId(undefined);
        setReportTagFilterId(undefined);
        setPage(1);
    }, [location.pathname]);

    const departmentScope = useMemo(() => {
        if (scope === 'NATION') return { nationId: scopeId };
        if (scope === 'DISTRICT') return { districtId: scopeId };
        return { churchId: scopeId };
    }, [scope, scopeId]);

    const { departments } = useDepartments({ all: true, ...departmentScope });
    const { data: reportTags = [] } = useReportTags({
        all: true,
        ...departmentScope,
    });

    useEffect(() => {
        if (leadershipType || !deptFilterId) return;
        const selected = departments.find(
            (department: { department_id: number; leadership_type?: string | null }) =>
                department.department_id === deptFilterId
        );
        if (selected?.leadership_type) {
            setDeptFilterId(undefined);
            setPage(1);
        }
    }, [departments, deptFilterId, leadershipType]);

    // ── API query params ────────────────────────────────────────────────────
    const queryParams = useMemo(() => ({
        reportStatusId,
        page,
        perPage,
        // freetext applies on ALL views — results are always bounded by the
        // locked status filter above (reportStatusId) when on a status tab.
        freetext: debouncedFreetext.trim() || undefined,
        departmentId: leadershipType ? undefined : deptFilterId,
        reportTagId: reportTagFilterId,
        leadershipType,
    }), [reportStatusId, page, perPage, debouncedFreetext, deptFilterId, reportTagFilterId, leadershipType]);

    // Wait for status resolution on non-all views
    const enabledScopeId = isAllView ? scopeId : (reportStatusId !== undefined ? scopeId : 0);

    const { data, loading, reload, pagination } = useDepartmentReports(
        scope,
        enabledScopeId,
        queryParams
    );

    const isLoading = statusesLoading || (!isAllView && reportStatusId === undefined) || loading;

    // ── Handlers ───────────────────────────────────────────────────────────
    const handlePageChange = (newPage: number) => setPage(newPage);

    const handlePerPageChange = (newPerPage: number) => {
        setPerPage(newPerPage);
        setPage(1);
    };

    const handleClearSearch = () => {
        setSearchFreetext('');
        setDeptFilterId(undefined);
        setReportTagFilterId(undefined);
        setPage(1);
    };

    const handleApplyFilters = (filters: Record<string, any>) => {
        if (!leadershipType) {
            setDeptFilterId(
                filters[REPORT_SPA_QUERY_PARAMS.DEPARTMENT_ID]
                    ? Number(filters[REPORT_SPA_QUERY_PARAMS.DEPARTMENT_ID])
                    : undefined
            );
        }
        setReportTagFilterId(
            filters[REPORT_SPA_QUERY_PARAMS.TAG_ID]
                ? Number(filters[REPORT_SPA_QUERY_PARAMS.TAG_ID])
                : undefined
        );
        setPage(1);
    };

    const paginationMeta = useMemo(() => {
        if (!pagination || pagination.total === 0) return null;
        return {
            current_page: pagination.current_page,
            last_page: pagination.last_page,
            per_page: pagination.per_page,
            total: pagination.total,
            from: pagination.from,
            to: pagination.to,
        };
    }, [pagination]);

    const handleRowClick = (item: typeof data[number]) => {
        if (!item.report_tag_id) return;

        const params = buildReportViewSearch({
            departmentId: item.department_id,
            reportTagId: item.report_tag_id,
        });

        if (leadershipType) {
            const sub = LEADERSHIP_SLUGS[leadershipType];
            navigate(`${basePath}/leadership-reports/${sub}/view?${params}`);
            return;
        }

        navigate(`${basePath}/reports/view?${params}`);
    };

    const filterFields = useMemo(() => {
        const periodField = {
            name: REPORT_SPA_QUERY_PARAMS.TAG_ID,
            label: 'Reporting Period',
            type: 'select' as const,
            options: reportTags.map((tag: any) => {
                const statusName = tag.report_tag_status_name as string | null | undefined;
                const statusLabel = getReportTagStatusLabel(statusName);
                const name = tag.name as string;
                return {
                    value: tag.report_tag_id,
                    label:
                        statusLabel && !isTagOpen(statusName)
                            ? `${name} (${statusLabel})`
                            : name,
                };
            }),
            colWidth: leadershipType ? 12 : 6,
        };

        if (leadershipType) {
            return [periodField];
        }

        const departmentOptions = departments
            .filter((department: { leadership_type?: string | null }) => !department.leadership_type)
            .map((department: { department_id: number; name: string }) => ({
                value: department.department_id,
                label: department.name,
            }));

        return [
            {
                name: REPORT_SPA_QUERY_PARAMS.DEPARTMENT_ID,
                label: 'Department',
                type: 'select' as const,
                options: departmentOptions,
                colWidth: 6,
            },
            periodField,
        ];
    }, [departments, reportTags, leadershipType]);

    const activeFilterCount =
        (!leadershipType && deptFilterId ? 1 : 0) + (reportTagFilterId ? 1 : 0);

    const handleValidateReport = async (item: typeof data[number]) => {
        if (!item.report_tag_id) return;

        const rowKey = `${item.department_id}-${item.report_tag_id}`;
        setProcessingKey(rowKey);

        try {
            const response = await processDepartmentReportAsync(
                scope === 'NATION'
                    ? {
                        scope: 'nation',
                        nationId: scopeId,
                        departmentId: item.department_id,
                        reportTagId: item.report_tag_id,
                    }
                    : scope === 'DISTRICT'
                        ? {
                            scope: 'district',
                            districtId: scopeId,
                            departmentId: item.department_id,
                            reportTagId: item.report_tag_id,
                        }
                        : {
                            scope: 'church',
                            churchId: scopeId,
                            departmentId: item.department_id,
                            reportTagId: item.report_tag_id,
                        }
            );

            if (!response.isSuccess()) {
                showError(response.getError() as ErrorResponse);
                return;
            }

            showSuccess(response.getData()?.message ?? 'Department report validated successfully.');
            await reload();
        } catch (error) {
            showError(error as ErrorResponse);
        } finally {
            setProcessingKey(null);
        }
    };

    // ── Render ─────────────────────────────────────────────────────────────

    return (
        <Container fluid className="py-4">
            {/* Page title */}
            <div className="d-flex align-items-center gap-2 mb-3">
                <FiFileText size={20} className="text-primary" />
                <h1 className="ntcog-page-heading mb-1">{leadershipType ? LEADERSHIP_TITLES[leadershipType] : "Department Reports"}</h1>
            </div>

            {/*
              ── Search bar ─────────────────────────────────────────────────
              Always visible. On status tabs, results are bounded by the locked
              reportStatusId — the user cannot override it from this UI.
            */}
            <div className="ntcog-list-search-section">
                <SearchBar
                    placeholder={
                        isAllView
                            ? 'Search all reports…'
                            : `Search within ${slugLabel.label} reports…`
                    }
                    initialQuery={searchFreetext}
                    initialFilters={buildReportListFilterSearch({
                        departmentId: leadershipType ? undefined : deptFilterId,
                        reportTagId: reportTagFilterId,
                    })}
                    onQueryChange={(q) => { setSearchFreetext(q); setPage(1); }}
                    filterFields={filterFields}
                    onFiltersApply={handleApplyFilters}
                    onClear={handleClearSearch}
                    loading={loading}
                    activeFilterCount={activeFilterCount}
                    testId="dept-reports-search"
                />

            </div>

            {/* ── Data ── */}
            <Card className="shadow-sm ntcog-card ntcog-list-table-card">
              <Card.Body className="p-0">
                <div className="d-none d-md-block">
                  <Table responsive hover size="sm" className="mb-0 align-middle table-striped ntcog-table">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '1%', whiteSpace: 'nowrap' }}>#</th>
                        <th>Department</th>
                        <th>Reporting Period</th>
                        <th>Period Range</th>
                        <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Status</th>
                        <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Last Updated</th>
                        <th style={{ width: '1%', whiteSpace: 'nowrap' }} className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="table-group-divider" style={{ opacity: isLoading && data.length > 0 ? 0.5 : 1, transition: 'opacity 0.2s ease' }}>
                      {isLoading && data.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                        <tr key={`ph-${i}`}>
                          <td><Placeholder xs={4} /></td>
                          <td><Placeholder xs={7} /></td>
                          <td><Placeholder xs={5} /></td>
                          <td><Placeholder xs={6} /></td>
                          <td><Placeholder xs={5} /></td>
                          <td><Placeholder xs={6} /></td>
                          <td><Placeholder xs={6} /></td>
                        </tr>
                      ))}
                      {!isLoading && data.length === 0 && (
                        <tr>
                          <td colSpan={7}>
                            <NoResult
                              line_1={`No ${slugLabel.label} Reports`}
                              line_2={
                                debouncedFreetext.trim() || deptFilterId
                                  ? 'No reports match your search. Try adjusting or clearing filters.'
                                  : isAllView
                                    ? 'There are no department reports at this time.'
                                    : `No "${slugLabel.label}" reports at this time.`
                              }
                            />
                          </td>
                        </tr>
                      )}
                      {data.map((item, i) => {
                        const rowNumber = paginationMeta?.from != null ? paginationMeta.from + i : i + 1;

                        return (
                        <tr
                          key={item.department_report_id}
                          onClick={() => handleRowClick(item)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="text-muted">{rowNumber}</td>
                          <td className="fw-semibold">{item.department_name}</td>
                          <td>{item.report_tag_name ?? '—'}</td>
                          <td className="text-muted small">
                            {item.report_tag_since || item.report_tag_until
                              ? `${item.report_tag_since ?? '—'} — ${item.report_tag_until ?? '—'}`
                              : '—'}
                          </td>
                          <td>
                            <Badge
                              {...subtleBadgeProps(getWorkflowStatusVariant(item.department_report_status_name))}
                            >
                              {getWorkflowStatusLabel(item.department_report_status_name)}
                            </Badge>
                            <div className="text-muted small mt-1">
                              {isDepartmentReportEditable(item.department_report_status_name)
                                ? 'Editable'
                                : 'View only'}
                            </div>
                          </td>
                          <td className="text-muted small">
                            {item.last_updated
                              ? new Date(item.last_updated).toLocaleDateString()
                              : '—'}
                          </td>
                          <td className="text-end">
                            <div className="d-inline-flex gap-2 flex-wrap justify-content-end">
                              <Button
                                variant={isDepartmentReportEditable(item.department_report_status_name) ? 'outline-primary' : 'outline-secondary'}
                                size="sm"
                                className="btn-action"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowClick(item);
                                }}
                              >
                                {isDepartmentReportEditable(item.department_report_status_name)
                                  ? 'Open Draft'
                                  : `View ${getWorkflowStatusLabel(item.department_report_status_name)}`}
                              </Button>
                              {canValidateDepartmentReport(item.department_report_status_name) && item.report_tag_id ? (
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  className="btn-action"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleValidateReport(item);
                                  }}
                                  disabled={
                                    processingKey === `${item.department_id}-${item.report_tag_id}`
                                  }
                                >
                                  {processingKey === `${item.department_id}-${item.report_tag_id}`
                                    ? 'Validating…'
                                    : normalizeReportWorkflowStatus(item.department_report_status_name) === 'NEEDS_ATTENTION'
                                      ? 'Re-Validate'
                                      : 'Validate'}
                                </Button>
                              ) : null}
                              {item.report_tag_id &&
                              normalizeReportWorkflowStatus(item.department_report_status_name) === "SUBMITTED" ? (
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="btn-action"
                                  title="Run validation manually for submitted reports"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleValidateReport(item);
                                  }}
                                  disabled={
                                    processingKey === `${item.department_id}-${item.report_tag_id}`
                                  }
                                >
                                  {processingKey === `${item.department_id}-${item.report_tag_id}`
                                    ? 'Validating…'
                                    : 'Manual Validate'}
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                {/* Mobile (< md): stacked cards */}
                <div className="ntcog-card-list d-md-none" data-testid="department-reports-cards">
                  {isLoading && data.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                    <div key={`ph-card-${i}`} className="ntcog-card-list__item">
                      <EntityCard
                        testId={`dept-report-card-skeleton-${i}`}
                        title={<Placeholder as="span" animation="glow"><Placeholder xs={6} /></Placeholder>}
                      />
                    </div>
                  ))}
                  {!isLoading && data.length === 0 && (
                    <NoResult
                      line_1={`No ${slugLabel.label} Reports`}
                      line_2={
                        debouncedFreetext.trim() || deptFilterId
                          ? 'No reports match your search. Try adjusting or clearing filters.'
                          : isAllView
                            ? 'There are no department reports at this time.'
                            : `No "${slugLabel.label}" reports at this time.`
                      }
                    />
                  )}
                  {data.map((item) => {
                    const editable = isDepartmentReportEditable(item.department_report_status_name);
                    const statusLabel = getWorkflowStatusLabel(item.department_report_status_name);
                    const badgeVariant = getWorkflowStatusVariant(item.department_report_status_name);
                    const processing = processingKey === `${item.department_id}-${item.report_tag_id}`;
                    const showValidate = canValidateDepartmentReport(item.department_report_status_name) && !!item.report_tag_id;
                    const showManual = !!item.report_tag_id
                      && normalizeReportWorkflowStatus(item.department_report_status_name) === "SUBMITTED";
                    return (
                      <div key={item.department_report_id} className="ntcog-card-list__item">
                        <EntityCard
                          testId={`dept-report-card-${item.department_report_id}`}
                          ariaLabel={`Report ${item.department_name}`}
                          title={<span className="fw-semibold">{item.department_name}</span>}
                          details={[
                            { label: "Reporting Period", value: item.report_tag_name ?? "—" },
                            {
                              label: "Period",
                              value: item.report_tag_since || item.report_tag_until
                                ? `${item.report_tag_since ?? "—"} — ${item.report_tag_until ?? "—"}`
                                : "—",
                            },
                            { label: "Updated", value: item.last_updated ? new Date(item.last_updated).toLocaleDateString() : "—" },
                          ]}
                          status={
                            <>
                              <Badge {...subtleBadgeProps(badgeVariant)}>{statusLabel}</Badge>
                              <span className="text-muted small">{editable ? "Editable" : "View only"}</span>
                            </>
                          }
                          actions={(showValidate || showManual) ? (
                            <TableActions aria-label={`Actions for ${item.department_name}`}>
                              {showValidate && (
                                <TableActionButton
                                  icon={<FiCheckCircle />}
                                  label={processing
                                    ? "Validating…"
                                    : normalizeReportWorkflowStatus(item.department_report_status_name) === "NEEDS_ATTENTION"
                                      ? "Re-Validate"
                                      : "Validate"}
                                  disabled={processing}
                                  onClick={() => handleValidateReport(item)}
                                />
                              )}
                              {showManual && (
                                <TableActionButton
                                  icon={<FiRefreshCw />}
                                  label={processing ? "Validating…" : "Manual Validate"}
                                  disabled={processing}
                                  onClick={() => handleValidateReport(item)}
                                />
                              )}
                            </TableActions>
                          ) : undefined}
                          primaryAction={
                            <Button
                              variant={editable ? "outline-primary" : "outline-secondary"}
                              size="sm"
                              className="btn-action w-100"
                              onClick={() => handleRowClick(item)}
                            >
                              {editable ? "Open Draft" : `View ${getWorkflowStatusLabel(item.department_report_status_name)}`}
                            </Button>
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              </Card.Body>
            </Card>

            {paginationMeta && paginationMeta.last_page > 1 && (
                <Pagination
                    meta={paginationMeta}
                    onPageChange={handlePageChange}
                    showInfo={true}
                    showPerPageSelector={true}
                    perPageOptions={[10, 20, 50, 100]}
                    onPerPageChange={handlePerPageChange}
                    className="mt-3"
                />
            )}
        </Container>
    );
};

export default DepartmentReportsList;
